/**
 * GeoUtils — the single place geometry math for safety routing lives. It is a thin layer over
 * `google.maps.geometry.spherical` and mirrors the corridor pattern already used in
 * BaseLayer.hideOutOfRangeMarkers() (distance of a point to every vertex of a route's overview_path).
 *
 * All functions are pure and take/return `google.maps.LatLng` instances or plain numbers, so they can
 * be unit-reasoned about without touching the DOM or the map.
 */

/* eslint-disable no-undef */

/**
 * @returns {google.maps.LatLng} the geographic position of a google.maps.Data.Feature point.
 */
export function featureLatLng(feature) {
  return feature.getGeometry().get();
}

/**
 * Distance in metres between two LatLng.
 */
export function distanceBetween(a, b) {
  return google.maps.geometry.spherical.computeDistanceBetween(a, b);
}

/**
 * Minimum distance (in metres) from a point to a polyline expressed as an array of vertices, plus the
 * index of the nearest vertex. Vertex-based (not segment-based) to stay consistent with the existing
 * radius filter; densify the path first (see densifyPath) when the polyline is coarse.
 *
 * @param {google.maps.LatLng} point
 * @param {google.maps.LatLng[]} path
 * @returns {{distance: number, index: number}}
 */
export function minDistanceToPath(point, path) {
  let min = Infinity;
  let minIndex = 0;
  for (let i = 0; i < path.length; i++) {
    const d = google.maps.geometry.spherical.computeDistanceBetween(point, path[i]);
    if (d < min) {
      min = d;
      minIndex = i;
      if (min === 0) {
        break;
      }
    }
  }
  return { distance: min, index: minIndex };
}

/**
 * Cumulative along-path distance (metres) for each vertex; cum[0] === 0 and the last entry is the
 * total path length.
 *
 * @param {google.maps.LatLng[]} path
 * @returns {number[]}
 */
export function cumulativeDistances(path) {
  const cum = new Array(path.length);
  cum[0] = 0;
  for (let i = 1; i < path.length; i++) {
    cum[i] = cum[i - 1] + google.maps.geometry.spherical.computeDistanceBetween(path[i - 1], path[i]);
  }
  return cum;
}

/**
 * Total length of a path in metres.
 */
export function pathLength(path) {
  if (!path || path.length < 2) {
    return 0;
  }
  return google.maps.geometry.spherical.computeLength(path);
}

/**
 * Returns a denser copy of `path` with intermediate points inserted so consecutive vertices are no
 * more than `spacing` metres apart. Google's overview_path is decimated, so on long motorway stretches
 * a vertex-based corridor test can miss PSRs that sit between two far-apart vertices; densifying fixes
 * that. Returns the original array if it is already dense enough.
 *
 * @param {google.maps.LatLng[]} path
 * @param {number} spacing metres
 * @returns {google.maps.LatLng[]}
 */
export function densifyPath(path, spacing = 1000) {
  if (!path || path.length < 2) {
    return path || [];
  }
  const dense = [];
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    dense.push(a);
    const segLength = google.maps.geometry.spherical.computeDistanceBetween(a, b);
    if (segLength > spacing) {
      const steps = Math.floor(segLength / spacing);
      for (let s = 1; s <= steps; s++) {
        dense.push(google.maps.geometry.spherical.interpolate(a, b, (s * spacing) / segLength));
      }
    }
  }
  dense.push(path[path.length - 1]);
  return dense;
}

/**
 * A point offset `distance` metres from `origin` along `heading` degrees. Used by the incident
 * nudging heuristic to build a lateral detour waypoint.
 */
export function computeOffset(origin, distance, heading) {
  return google.maps.geometry.spherical.computeOffset(origin, distance, heading);
}

/**
 * Heading in degrees from `from` to `to`.
 */
export function computeHeading(from, to) {
  return google.maps.geometry.spherical.computeHeading(from, to);
}

/**
 * Mean position of a list of LatLng (simple arithmetic centroid; fine at the scales we use it).
 *
 * @param {google.maps.LatLng[]} points
 * @returns {google.maps.LatLng|null}
 */
export function centroid(points) {
  if (!points || !points.length) {
    return null;
  }
  let lat = 0;
  let lng = 0;
  points.forEach((p) => {
    lat += p.lat();
    lng += p.lng();
  });
  return new google.maps.LatLng(lat / points.length, lng / points.length);
}
